/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { Keys } from '#utils/UNSAFE_keys';
import type { TableColumn } from './Table';

import { classNames } from '#utils/UNSAFE_classNames';
import { containsKey } from '#utils/UNSAFE_keys';
import { backgroundStyles, tableFooterRowStyles } from './themes/Table.css';
import { TableFooterCell } from './TableFooterCell';

/**
 * Props for the TableFooter Component
 */
type TableFooterProps<K, D, C> = {
  tableId: string;
  isRendered: boolean;
  columnsArray: TableColumn<K, D, C>[];
  hasVerticalGridlines: boolean;
  activeKey?: C;
  isTabbableMode: boolean;
  isShowFocusRing: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  selectedColumnKeys?: Keys<C>;
  isColumnSelectionEnabled: boolean;
  activeStyleKey?: C;
  hoverStyleKey?: C;
  stickyBottomOffset?: number;
  startStickyEdge?: number;
  endStickyEdge?: number;
  firstStickyColumnIndex?: number;
  finalStickyColumnIndex?: number;
  columnBorderEdges: { hasStart: boolean; hasEnd: boolean; width: number }[];
  dataRowCount: number;
};

/**
 * The internal component used to render a footer region in Table.
 */
export function TableFooter<K, D, C>({
  tableId,
  isRendered,
  columnsArray,
  hasVerticalGridlines,
  activeKey,
  isTabbableMode,
  isShowFocusRing,
  selectedColumnKeys,
  isColumnSelectionEnabled,
  activeStyleKey,
  hoverStyleKey,
  stickyBottomOffset,
  startStickyEdge,
  endStickyEdge,
  firstStickyColumnIndex,
  finalStickyColumnIndex,
  columnBorderEdges,
  dataRowCount,
  isRtl,
  isHighContrast
}: TableFooterProps<K, D, C>) {
  const rowClasses = classNames([tableFooterRowStyles.base]);

  let previousColumnKey: C | undefined;
  let previousStickyColumnKey: C | undefined;
  let isPreviousSelectable = false;
  let isPreviousStickySelectable = false;
  let isPreviousColumnSelected = false;
  let isPreviousStickyColumnSelected = false;
  return isRendered ? (
    <tfoot class={backgroundStyles.inherit} role={'presentation'}>
      <tr
        aria-rowindex={Math.max(dataRowCount, 1) + 2}
        class={rowClasses}
        role={'row'}
        data-oj-table-footer-row={tableId}>
        {columnsArray.map((column: TableColumn<K, D, C>, columnIndex: number) => {
          const columnKey = column.key;
          const isSticky = column.value.sticky === 'enabled';
          const isSelectable = isColumnSelectionEnabled && column.value.selectable !== 'disabled';
          const isSelected = containsKey(selectedColumnKeys, columnKey);
          const isActive = columnKey === activeKey;
          const hasTabIndex = isActive && !isTabbableMode;
          let previousVisibleColumnKey;
          let isPreviousVisibleColumnSelectable = false;
          let isPreviousVisibleColumnSelected = false;
          let nextVisibleColumn;
          let isNextVisibleColumnSelected = false;
          let isFirstLogicalColumnIndex = false;
          let isFinalLogicalColumnIndex = false;
          if (isSticky) {
            if (startStickyEdge != null && firstStickyColumnIndex === columnIndex) {
              isFirstLogicalColumnIndex = true;
            }
            if (endStickyEdge != null && finalStickyColumnIndex === columnIndex) {
              isFinalLogicalColumnIndex = true;
            }
            if (
              (startStickyEdge != null && startStickyEdge > columnIndex) ||
              (endStickyEdge != null && endStickyEdge <= columnIndex)
            ) {
              for (let i = columnIndex + 1; i < columnsArray.length; i++) {
                if (columnsArray[i].value.sticky === 'enabled') {
                  nextVisibleColumn = columnsArray[i];
                  break;
                }
              }
            } else {
              nextVisibleColumn =
                columnIndex < columnsArray.length - 1 ? columnsArray[columnIndex + 1] : undefined;
            }
            isNextVisibleColumnSelected =
              startStickyEdge === columnIndex
                ? false
                : nextVisibleColumn != null &&
                  containsKey(selectedColumnKeys, nextVisibleColumn.key);
            if (
              (startStickyEdge != null && startStickyEdge >= columnIndex) ||
              (endStickyEdge != null && endStickyEdge < columnIndex)
            ) {
              previousVisibleColumnKey = previousStickyColumnKey;
              isPreviousVisibleColumnSelectable = isPreviousStickySelectable;
              isPreviousVisibleColumnSelected = isPreviousStickyColumnSelected;
            } else if (endStickyEdge !== columnIndex) {
              previousVisibleColumnKey = previousColumnKey;
              isPreviousVisibleColumnSelectable = isPreviousSelectable;
              isPreviousVisibleColumnSelected = isPreviousColumnSelected;
            }
          } else {
            previousVisibleColumnKey = previousColumnKey;
            isPreviousVisibleColumnSelectable = isPreviousSelectable;
            isPreviousVisibleColumnSelected = isPreviousColumnSelected;
          }
          const footerCellProps = {
            tableId: tableId,
            columnKey: columnKey,
            columnIndex: columnIndex,
            isFinalColumnIndex: columnIndex === columnsArray.length - 1,
            isFirstLogicalColumnIndex: isFirstLogicalColumnIndex,
            isFinalLogicalColumnIndex: isFinalLogicalColumnIndex,
            hasVerticalGridlines: hasVerticalGridlines,
            stickyBottomOffset: stickyBottomOffset,
            isSticky: isSticky,
            isSelected: isSelected,
            isNextColumnSelected: isNextVisibleColumnSelected,
            isPreviousColumnSelected: isPreviousVisibleColumnSelected,
            hasStartEdge: columnBorderEdges[columnIndex].hasStart,
            hasEndEdge: columnBorderEdges[columnIndex].hasEnd,
            isColumnSelectable: isSelectable,
            isShowFocusRing: isShowFocusRing && isActive,
            hasTabIndex: hasTabIndex,
            isTabbableMode: isTabbableMode,
            footerText: column.value.footerText,
            renderer: column.value.footerRenderer,
            isStickyStartEdge: columnIndex === startStickyEdge,
            isStickyEndEdge: columnIndex === endStickyEdge,
            isActiveStyle: isSelectable && activeStyleKey === columnKey,
            isHoverStyle: isSelectable && hoverStyleKey === columnKey,
            isPreviousCellActiveStyle:
              activeStyleKey != null &&
              activeStyleKey === previousVisibleColumnKey &&
              isPreviousVisibleColumnSelectable,
            isPreviousCellHoverStyle:
              hoverStyleKey != null &&
              hoverStyleKey === previousVisibleColumnKey &&
              isPreviousVisibleColumnSelectable,
            isRtl: isRtl,
            isHighContrast: isHighContrast,
            padding: column.value.footerPadding,
            tooltip: column.value.footerTooltip,
            focusTarget: column.value.footerFocusTarget,
            horizontalAlignment:
              column.value.footerAlignment?.horizontal != null
                ? column.value.footerAlignment.horizontal
                : column.value.horizontalAlignment,
            verticalAlignment: column.value.footerAlignment?.vertical
          };
          previousColumnKey = columnKey;
          isPreviousSelectable = isSelectable;
          isPreviousColumnSelected = isSelected;
          if (isSticky) {
            previousStickyColumnKey = columnKey;
            isPreviousStickySelectable = isSelectable;
            isPreviousStickyColumnSelected = isSelected;
          }
          return <TableFooterCell {...footerCellProps} />;
        })}
      </tr>
    </tfoot>
  ) : null;
}
